import React from 'react';

// Generic loading spinner
export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; className?: string }> = ({
  size = 'md',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
  };

  return (
    <div className={`animate-spin ${sizeClasses[size]} ${className}`}>
      <svg
        className="h-full w-full text-current"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};

// Content card skeleton for loading states
export const ContentCardSkeleton: React.FC = () => {
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden animate-pulse">
      <div className="aspect-[2/3] bg-gray-700" />
      <div className="p-3">
        <div className="h-4 bg-gray-700 rounded mb-2" />
        <div className="h-3 bg-gray-700 rounded w-3/4 mb-1" />
        <div className="h-3 bg-gray-700 rounded w-1/2" />
      </div>
    </div>
  );
};

// Content carousel skeleton
export const ContentCarouselSkeleton: React.FC<{ title?: string }> = ({ title }) => {
  return (
    <div className="mb-8">
      {title && (
        <h2 className="text-xl font-semibold text-white mb-4">{title}</h2>
      )}
      <div className="flex space-x-4 overflow-hidden">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="flex-shrink-0 w-48">
            <ContentCardSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
};

// Content grid skeleton
export const ContentGridSkeleton: React.FC<{ count?: number }> = ({ count = 12 }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {Array.from({ length: count }).map((_, index) => (
        <ContentCardSkeleton key={index} />
      ))}
    </div>
  );
};

// Hero section skeleton
export const HeroSkeleton: React.FC = () => {
  return (
    <div className="relative h-96 bg-gray-800 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-transparent" />
      <div className="absolute bottom-0 left-0 p-8">
        <div className="h-8 bg-gray-700 rounded w-64 mb-4" />
        <div className="h-4 bg-gray-700 rounded w-96 mb-2" />
        <div className="h-4 bg-gray-700 rounded w-80 mb-4" />
        <div className="flex space-x-4">
          <div className="h-10 bg-gray-700 rounded w-24" />
          <div className="h-10 bg-gray-700 rounded w-32" />
        </div>
      </div>
    </div>
  );
};

// Search results skeleton
export const SearchResultsSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="flex space-x-4 p-4 bg-gray-800 rounded-lg animate-pulse">
          <div className="w-16 h-24 bg-gray-700 rounded flex-shrink-0" />
          <div className="flex-1">
            <div className="h-5 bg-gray-700 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-700 rounded w-1/2 mb-2" />
            <div className="h-3 bg-gray-700 rounded w-2/3 mb-2" />
            <div className="h-3 bg-gray-700 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Page loading overlay
export const PageLoadingOverlay: React.FC<{ message?: string }> = ({
  message = 'Loading...',
}) => {
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
        <LoadingSpinner size="lg" className="text-red-500" />
        <span className="text-white font-medium">{message}</span>
      </div>
    </div>
  );
};

// Inline loading state for buttons
export const ButtonLoading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex items-center space-x-2">
      <LoadingSpinner size="sm" />
      <span>{children}</span>
    </div>
  );
};

// Loading state for content details
export const ContentDetailsSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse">
      <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-8">
        {/* Poster */}
        <div className="w-full md:w-80 aspect-[2/3] bg-gray-700 rounded-lg flex-shrink-0" />
        
        {/* Details */}
        <div className="flex-1">
          <div className="h-8 bg-gray-700 rounded w-3/4 mb-4" />
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-2" />
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-4" />
          
          {/* Synopsis */}
          <div className="space-y-2 mb-6">
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded w-3/4" />
          </div>
          
          {/* Cast */}
          <div className="h-5 bg-gray-700 rounded w-24 mb-3" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-700 rounded w-1/2" />
            <div className="h-3 bg-gray-700 rounded w-2/3" />
            <div className="h-3 bg-gray-700 rounded w-1/3" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading state with retry option
export const LoadingWithRetry: React.FC<{
  isLoading: boolean;
  error?: Error | null;
  onRetry: () => void;
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
}> = ({ isLoading, error, onRetry, children, loadingComponent }) => {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <svg
            className="h-12 w-12 text-gray-400 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-gray-400 mb-4">
          {error.message || 'Something went wrong'}
        </p>
        <button
          onClick={onRetry}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        {loadingComponent || <LoadingSpinner size="lg" className="text-red-500" />}
      </div>
    );
  }

  return <>{children}</>;
};

export default LoadingSpinner;